/**
 * Proxy URL rewriting for jupyter-server-proxy embedded apps.
 *
 * Production: https://{sessionId}.proxy.propalgos.ai/proxy/{port}/
 * Dev:        http://localhost:8000/api/v1/notebooks/proxy/{sessionId}/proxy/{port}/
 *
 * Notebooks emit /proxy/{port}/ links (the jupyter-server-proxy convention).
 * In native JupyterLab these resolve natively. In our custom NativeNotebookPage
 * the browser resolves them against the app origin, so we rewrite them.
 */

import { getApiUrl } from './api';

const PROXY_DOMAIN = 'proxy.propalgos.ai';

function isProduction(): boolean {
  const hostname = window.location.hostname;
  return hostname !== 'localhost' && !hostname.startsWith('127.');
}

/**
 * Construct the proxy origin for a given session.
 * Production: https://{sessionId}.proxy.propalgos.ai
 * Dev: same-origin backend URL (path-based fallback)
 */
export function getProxyOrigin(sessionId: string): string {
  if (isProduction()) {
    return `https://${sessionId}.${PROXY_DOMAIN}`;
  }
  return getApiUrl();
}

/**
 * Build the full URL to initiate proxy auth for a session.
 * The _auth endpoint sets the session cookie, then redirects to the target.
 */
export function buildProxyAuthUrl(
  sessionId: string,
  authCode: string,
  redirectPath: string = '/proxy/8501/',
): string {
  const encodedCode = encodeURIComponent(authCode);
  const encodedRedirect = encodeURIComponent(redirectPath);

  if (isProduction()) {
    const origin = getProxyOrigin(sessionId);
    return `${origin}/_auth?code=${encodedCode}&redirect=${encodedRedirect}`;
  }
  // Dev: use path-based auth exchange on the backend
  const apiUrl = getApiUrl();
  const devRedirect = `/api/v1/notebooks/proxy/${sessionId}${redirectPath}`;
  return `${apiUrl}/api/v1/notebooks/proxy-auth-exchange/${sessionId}?code=${encodedCode}&redirect=${encodeURIComponent(devRedirect)}`;
}

/**
 * Rewrite /proxy/{port}/ URLs in HTML content to route through the proxy.
 * Production: rewrites to subdomain origin (paths preserved as-is)
 * Dev: rewrites to path-based backend proxy
 */
export function rewriteProxyUrls(html: string, sessionId: string): string {
  const origin = getProxyOrigin(sessionId);
  if (isProduction()) {
    return html.replace(
      /((?:src|href|action)=["'])\/proxy\/(\d+)(\/[^"']*)?(?=["'])/g,
      `$1${origin}/proxy/$2$3`,
    );
  }
  const proxyBase = `${origin}/api/v1/notebooks/proxy/${sessionId}`;
  return html.replace(
    /((?:src|href|action)=["'])\/proxy\/(\d+)(\/[^"']*)?(?=["'])/g,
    `$1${proxyBase}/proxy/$2$3`,
  );
}

/**
 * Rewrite proxy iframe srcs and anchor hrefs in already-rewritten HTML
 * to go through the auth exchange endpoint. The auth exchange sets a
 * session cookie and 302-redirects to the actual proxy URL.
 *
 * - iframe src → auth exchange URL (consumes the auth code, sets cookie)
 * - anchor href → /proxy-window?sessionId=...&port=... (StandaloneProxyPage
 *   fetches its own fresh auth code on mount, avoiding single-use expiry)
 *
 * Call this AFTER rewriteProxyUrls — it operates on fully-qualified proxy URLs.
 */
export function rewriteProxyForAuth(
  html: string,
  sessionId: string,
  authCode: string,
): string {
  // Rewrite iframe src= to auth exchange URL
  const iframeSrcPattern = /(src=["'])(https?:\/\/[^"']*\/proxy\/(\d+)\/[^"']*)(?=["'])/g;
  html = html.replace(iframeSrcPattern, (_match, prefix, url, port) => {
    const pathMatch = url.match(/\/proxy\/\d+\/.*/);
    const redirectPath = pathMatch ? pathMatch[0] : `/proxy/${port}/`;
    const authUrl = buildProxyAuthUrl(sessionId, authCode, redirectPath);
    return `${prefix}${authUrl}`;
  });

  // Rewrite anchor href= to /proxy-window (fetches its own fresh auth code)
  const anchorHrefPattern = /(href=["'])(https?:\/\/[^"']*\/proxy\/(\d+)\/[^"']*)(?=["'])/g;
  html = html.replace(anchorHrefPattern, (_match, prefix, _url, port) => {
    const params = new URLSearchParams({ sessionId, port });
    return `${prefix}/proxy-window?${params.toString()}`;
  });

  return html;
}

/**
 * Check whether HTML contains proxy URLs (useful for deciding whether to
 * apply auth rewriting).
 */
export function containsProxyContent(html: string): boolean {
  return /\/proxy\/\d+\//.test(html);
}

/**
 * Rewrite a single /proxy/{port}/ URL (for markdown link hrefs).
 * Returns the rewritten URL, or null if the href isn't a proxy link.
 */
export function rewriteProxyLink(href: string, sessionId: string): string | null {
  const match = href.match(/^\/proxy\/(\d+)(\/.*)?$/);
  if (!match) return null;

  const port = match[1];
  const rest = match[2] || '/';
  const origin = getProxyOrigin(sessionId);

  if (isProduction()) {
    return `${origin}/proxy/${port}${rest}`;
  }
  return `${origin}/api/v1/notebooks/proxy/${sessionId}/proxy/${port}${rest}`;
}
